import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import { ThemeProperties } from "../../../Theme/ThemeProperties";

import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import EntityTypeTabs from "./EntityTypeTabs";
import CreateEditEntityDialog from "./CreateEditEntityDialog";
import useEntityTypeConfig from "./hooks/useEntityTypeConfig";
import useNetworkEntities from "./hooks/useNetworkEntities";
import useSelectAllAcrossPages from "../hooks/useSelectAllAcrossPages";
import {
    buildCSVRows,
    buildFilterDefs,
    getDefaultSelectedColumns,
    mapSelectedFiltersToParams,
} from "./tableHelpers";
import {
    emailNetworkEntities,
    exportNetworkEntities,
    getNetworkEntitySearchSuggestions,
} from "../../../Api/NetworkEntityApi";
import { getOrgLocationMaster } from "../../../Api/RbacAdminApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { useToast } from "../../../Hooks/useToast";
import { getAvailableEmailDomains } from "../../../Helper/Organization/EmailDomains";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ASSET_MANAGEMENT_LABEL } from "../../../Static/ModuleLabels";

const LOCATION_LEVELS = ["level1", "level2", "level3", "level4", "level5"];

const locationOptionLabel = (row) =>
    LOCATION_LEVELS.map((lv) => row?.[lv]).filter(Boolean).join(" / ");

// The Network tab: one sub-tab per configured entity type, each rendering that
// type's own table.
//
// Everything here is config-driven. Which tabs exist, what they are called,
// which columns each shows and which fields the form has all come from the
// merged pageMeta/orgMeta payload. Adding an entity type is a config change.

const NetworkTabContent = ({
    metaInfo,
    canCreate = true,
    canEdit = true,
}) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { types, getTableConfig, getSearchableFields, isConfigured } =
        useEntityTypeConfig(metaInfo);

    // The sub-tab, seeded from the URL. Coming BACK from an entity page has to
    // land on the tab that entity belongs to -- returning a vendor to the
    // Customers tab makes the reader hunt for where they just were. The
    // parameter is only a seed: once set, the user's own tab choice wins, and
    // a code the org does not have falls through to the first type below.
    const [activeCode, setActiveCode] = useState(
        () => searchParams.get("entityType") || null
    );

    // Default to the first configured type. Held in state rather than derived
    // so the user's choice survives a metaInfo refresh.
    useEffect(() => {
        if (!searchParams.get("entityType")) return;
        const next = new URLSearchParams(searchParams);
        next.delete("entityType");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const activeType = useMemo(
        () => types.find((t) => t.code === activeCode) || types[0] || null,
        [types, activeCode]
    );

    const {
        records, total, isLoading, errorMessage,
        page, limit, setPage, applySearch, applySort, applyFilterParams,
        requestArgs, refetch,
    } = useNetworkEntities(activeType?.code);

    const { openNotification } = useToast();

    // globalConfig is copied onto every page's metaInfo by the backend, so the
    // domain list is already in hand -- adding one for an org is a DB change.
    const emailDomains = useMemo(
        () => getAvailableEmailDomains(metaInfo),
        [metaInfo]
    );

    const [dialogEntity, setDialogEntity] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const openCreate = useCallback(() => {
        setDialogEntity(null);
        setDialogOpen(true);
    }, []);

    const openEdit = useCallback((record) => {
        setDialogEntity(record);
        setDialogOpen(true);
    }, []);

    // --- row selection ----------------------------------------------------
    // Selection drives the batch actions in the table header: with rows ticked
    // the download and the email cover exactly those rows, with the header
    // ticked (select-all mode) every row matching the filters, and with none
    // ticked the whole filtered set (alwaysEnableActions), so the controls are
    // never a dead button waiting on a tick. The export endpoint narrows by
    // resourceIDs / selectAll -- the Inventory contract -- so the client never
    // filters the file itself. See ../selectionHelpers.js.
    const {
        requestFields: selectionFields,
        capError: selectionCapError,
        clearSelection,
        rowSelection,
        getTotalRowSelection,
        getTotalRowSelectionCount,
    } = useSelectAllAcrossPages({ records, total });

    // Row actions are dispatched by the `type` declared in pageMeta's
    // action-menu config. ActionMenu only knows a few hardcoded types, so these
    // are handled here via customRenderers rather than by adding branches to
    // the shared component.
    //
    // Signature is POSITIONAL: TableColumnValueRenderer.jsx:70-71 calls
    // customRenderers[type](record, config). Destructuring `record` off the
    // record object itself yielded undefined -- Edit silently opened a CREATE
    // form. Buttons, not clickable Typography, so the actions are keyboard
    // reachable.
    //
    // NO Remove here on purpose. Soft delete still exists end to end -- the
    // DELETE endpoint, its approver-only permission and DeleteEntityDialog are
    // all intact -- but the row action is withheld until the work-order flow
    // can say what removing an entity does to records that reference it.
    // Re-exposing it is adding one button back, not rebuilding a feature.
    const customRenderers = useMemo(() => ({
        "action-menu": (record) => (
            <Box sx={{ display: "flex", gap: 0.5 }}>
                <Button
                    size="small"
                    data-testid={`network-entity-view-${record?.id}`}
                    // THE ROUTE, not local state. The same view is reached
                    // from the Assets tab's Current Custody cell, so it has an
                    // address and both entry points use it.
                    onClick={() => navigate(`/asset-management/network/${record?.id}`)}
                    sx={{ fontSize: 12, minWidth: 0, px: 1,
                          color: ThemeProperties.purple, textTransform: "none" }}
                >
                    View
                </Button>
                {canEdit && (
                    <Button
                        size="small"
                        data-testid={`network-entity-edit-${record?.id}`}
                        onClick={() => openEdit(record)}
                        sx={{ fontSize: 12, minWidth: 0, px: 1,
                              color: ThemeProperties.purple, textTransform: "none" }}
                    >
                        Edit
                    </Button>
                )}
            </Box>
        ),
    }), [openEdit, canEdit, navigate]);

    const tableConfig = useMemo(
        () => getTableConfig(activeType),
        [activeType, getTableConfig]
    );

    const columnsConfig = tableConfig?.columnsConfig || null;
    const columnOrdering = useMemo(
        () => tableConfig?.columnOrdering || [],
        [tableConfig]
    );

    // Every column the picker may offer, actions included. `allColumns` feeds
    // the picker and `tableColumns` the table, so the visible set is a subset
    // of this rather than a second build with its own rules.
    const allColumns = useMemo(() => {
        if (!columnOrdering.length) return [];
        return getColumnsConfig({
            // Passed straight through: getColumnsConfig already does the named
            // lookup by key. Re-keying here collapsed every column onto
            // byKey[undefined] if any config leaf lacked `key`.
            tableColumns: columnsConfig,
            tableColumnOrder: columnOrdering,
            // Always shown: View needs no permission beyond the one that
            // loaded the page, so the column is never empty.
            shouldShowActionMenu: () => true,
            customRenderers,
        });
    }, [columnsConfig, columnOrdering, customRenderers]);

    // The actions column is never in the picker: it is not data, and letting a
    // user hide it would take away Edit and Remove with no way back.
    const pickableColumns = useMemo(
        () => allColumns.filter((c) => c.valueRenderingType !== "action-menu"),
        [allColumns]
    );

    const defaultSelectedColumns = useMemo(
        () => getDefaultSelectedColumns(columnsConfig, columnOrdering),
        [columnsConfig, columnOrdering]
    );

    const [selectedColumns, setSelectedColumns] = useState([]);

    // Re-seed on every tab switch: each entity type has its own columns, and a
    // selection carried across would hide every column of the new type.
    useEffect(() => {
        setSelectedColumns(defaultSelectedColumns);
    }, [defaultSelectedColumns]);

    const columns = useMemo(() => {
        // Matched on dataIndex, because that is what TableColumns writes into
        // selectedColumns. Matching on the config key instead works only while
        // key and dataIndex happen to be the same string, and fails silently
        // and totally the moment a config makes them differ.
        const visible = columnOrdering
            .map((key) => pickableColumns.find((c) => c.key === key))
            .filter((c) => c && selectedColumns.includes(c.dataIndex));
        const actions = allColumns.filter(
            (c) => c.valueRenderingType === "action-menu"
        );
        return [...visible, ...actions];
    }, [columnOrdering, selectedColumns, pickableColumns, allColumns]);

    // Header labels, in the order shown. GenericDraggableTable reorders the CSV
    // by this list, so anything missing from it becomes an empty column.
    //
    // `title` is only a fallback where it is still a string: getColumnsConfig
    // replaces it with a render function for some column types, and a function
    // in this list becomes an empty CSV column rather than an error.
    const headerLabelOf = useCallback(
        (c) => (typeof c.headerLabel === "string" ? c.headerLabel
            : typeof c.title === "string" ? c.title : c.dataIndex),
        []
    );

    const [draggedOrder, setDraggedOrder] = useState(null);

    // The table keeps drag order in its own state, so without this the CSV
    // stays in the pre-drag order while the screen shows the new one.
    const onColumnOrderChange = useCallback(
        (cols) =>
            setDraggedOrder(
                (cols || [])
                    .filter((c) => c.valueRenderingType !== "action-menu")
                    .map(headerLabelOf)
            ),
        [headerLabelOf]
    );

    // Dropped whenever the visible set changes: a drag order captured before a
    // column was hidden names a column the file no longer has.
    useEffect(() => {
        setDraggedOrder(null);
    }, [selectedColumns, activeType?.code]);

    const columnOrder = useMemo(
        () =>
            draggedOrder ||
            columns
                .filter((c) => c.valueRenderingType !== "action-menu")
                .map(headerLabelOf),
        [columns, draggedOrder, headerLabelOf]
    );

    const searchableFields = useMemo(
        () => getSearchableFields(activeType),
        [activeType, getSearchableFields]
    );

    // GenericDraggableTable.js:142 calls onSortChange(columnKey, "asc"|"desc"),
    // already normalised. The previous handleSort(sorter) received the key
    // STRING, so order was always null and sorting never applied.
    const handleSort = useCallback(
        (key, order) => applySort(key || null, order || null),
        [applySort]
    );

    // --- filters ----------------------------------------------------------
    // Location options are fetched once per tab, and only for a type that
    // actually declares a location property -- a Vendor has no location column,
    // so the request would be spent on a filter that is never offered.
    const [locationOptions, setLocationOptions] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({});

    const needsLocations = useMemo(
        () => (activeType?.properties || []).some((p) => p.type === "location"),
        [activeType]
    );

    useEffect(() => {
        if (!needsLocations) {
            setLocationOptions([]);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            const res = await getOrgLocationMaster({});
            if (cancelled) return;
            const rows =
                res?.responseStatus?.code === CODE.SUCCESS
                    ? res?.response?.data || []
                    : [];
            setLocationOptions(
                (Array.isArray(rows) ? rows : []).map((row) => ({
                    id: row.id,
                    value: locationOptionLabel(row),
                }))
            );
        })();
        return () => {
            cancelled = true;
        };
    }, [needsLocations, activeType?.code]);

    // Clearing on a tab switch is not cosmetic: the location ids belong to the
    // previous type's option list, and carrying them over would filter the new
    // tab by a selection its panel never showed.
    useEffect(() => {
        setSelectedFilters({});
        // Selection too. Ticked rows carry the previous type's ids, and the
        // export filters its rows by them -- so a download after a tab switch
        // produced an empty file with nothing to explain it.
        clearSelection();
    }, [activeType?.code, clearSelection]);

    const filters = useMemo(
        () => buildFilterDefs(activeType, locationOptions),
        [activeType, locationOptions]
    );

    const handleFiltersApply = useCallback(
        (next) => {
            setSelectedFilters(next || {});
            applyFilterParams(mapSelectedFiltersToParams(next || {}));
        },
        [applyFilterParams]
    );

    // --- export -----------------------------------------------------------
    // Requests the SAME search/sort/filter arguments the list is showing, so
    // the file can never cover a different set of rows than the screen. The
    // header labels and date formatting are applied from the very column config
    // the table renders with (buildCSVRows), which is what keeps the two in
    // step rather than a second formatter on the server.
    const getCSVData = useCallback(async () => {
        // More hand-ticked ids than the server accepts: say so and send
        // nothing. Thrown so onDownloadCSV writes no file and the email
        // (which builds its rows from here) is not sent.
        if (selectionCapError) {
            openNotification({ text: selectionCapError, icon: "toast-error" });
            throw new Error(selectionCapError);
        }
        const res = await exportNetworkEntities({ ...requestArgs, ...selectionFields });
        if (res?.responseStatus?.code !== CODE.SUCCESS) {
            openNotification({
                text: res?.responseStatus?.msg || "Could not prepare the download",
                icon: "toast-error",
            });
            return [];
        }
        if (res?.data?.truncated) {
            openNotification({
                text: `Only the first ${res.data.limit} rows were exported. Narrow the filters for the rest.`,
                icon: "toast-error",
            });
        }
        // Already scoped to the selection by the server.
        return buildCSVRows(res?.data?.records || [], columns);
    }, [requestArgs, selectionFields, selectionCapError, columns, openNotification]);

    // The emailed attachment is built from getCSVData, so it is byte for byte
    // the file the Download button produces -- selection included, since the
    // export it reads is already narrowed by resourceIDs / selectAll -- and
    // the server only addresses and sends it. A second CSV builder there would be free to drift from the one
    // the user can see.
    const onSendEmail = useCallback(
        async ({ sendTo, sendCc, subject, body }) => {
            const rows = await getCSVData();
            const res = await emailNetworkEntities({
                sendTo,
                sendCc,
                subject,
                body,
                rows,
                fileName: `${activeType?.labelPlural || "network-entities"}`,
            });
            if (res?.responseStatus?.code !== CODE.SUCCESS) {
                const message =
                    res?.responseStatus?.msg || "Could not send the email";
                // Toasted AND thrown. The throw keeps the composer open so the
                // user does not lose what they typed; the toast is what they
                // actually see, because the composer swallows the rejection
                // into console.error.
                openNotification({ text: message, icon: "toast-error" });
                throw new Error(message);
            }
        },
        [getCSVData, activeType, openNotification]
    );

    const getCSVFileName = useCallback(
        () =>
            `${(activeType?.labelPlural || activeType?.label || "network-entities")
                .replace(/\s+/g, "-")
                .toLowerCase()}.csv`,
        [activeType]
    );

    // "Not configured" is a real state with a real cause, not an empty list.
    // Saying so beats an empty tab strip that looks like a bug.
    if (!isConfigured) {
        return (
            <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography sx={{ fontSize: 14, color: ThemeProperties.greyTextColor }}>
                    Network entity types are not configured for this organisation.
                </Typography>
                <Typography sx={{ fontSize: 13, color: ThemeProperties.greyTextColor, mt: 0.5 }}>
                    They are defined in org settings under
                    AssetManagement &rarr; network &rarr; entityDefinition.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <EntityTypeTabs
                types={types}
                activeCode={activeType?.code}
                onChange={setActiveCode}
            />

            <Box
                sx={{
                    // No top margin: the tab strip owns the gap below itself,
                    // the way Inventory's does. Two margins meeting here is how
                    // the band between the tabs and the card grew to 24px.
                    "& .ant-table-column-sorter": { display: "none" },
                    "& .ant-table-column-has-sorters .ant-table-cell-content": {
                        cursor: "pointer",
                    },
                }}
            >
                <GenericDraggableTableView
                    // Remounted per entity type. TableSearch seeds its selected
                    // column from searchableFields[0] in useState, ONCE
                    // (TableSearch.jsx:155), so switching tabs left the previous
                    // type's key selected -- "Customer ID" has no counterpart
                    // under Vendors, and the control rendered "undefined".
                    // Remounting also drops the previous tab's chips and filter
                    // panel state, which the query hook was already clearing.
                    key={activeType?.code}
                    title={activeType?.labelPlural || activeType?.label || "Entities"}
                    count={total}
                    showPrimaryButton={canCreate}
                    primaryButtonLabel={`+ Add ${activeType?.label || "Entity"}`}
                    onPrimaryButtonClick={openCreate}
                    tableColumns={columns}
                    tableData={records}
                    isLoading={isLoading}
                    pageNumber={page}
                    rowsPerPage={limit}
                    total={total}
                    onPageChange={setPage}
                    applySort={handleSort}
                    applySearch={(selected, column) =>
                        // TableSearch hands back an ARRAY of chips; the list
                        // endpoint takes one `search` string, and an array
                        // serialises to search[0]=..&search[1]=.. which
                        // unknown=RAISE rejects.
                        applySearch(
                            Array.isArray(selected)
                                ? selected.filter(Boolean).join(",")
                                : selected,
                            column
                        )
                    }
                    searchableFields={searchableFields}
                    // The shared default fires the digital-twin device
                    // endpoint, which would offer battery IDs while the user
                    // types a warehouse name. This one is scoped to the active
                    // entity type and to the same column allowlist the search
                    // itself uses.
                    searchSuggestionsHandler={(term, columnName, limit) =>
                        getNetworkEntitySearchSuggestions(
                            activeType?.code,
                            term,
                            columnName,
                            limit
                        )
                    }
                    disableSearch={!searchableFields.length}
                    filters={filters}
                    selectedFilters={selectedFilters}
                    applyFilters={handleFiltersApply}
                    filteredCount={total}
                    initialColumns={pickableColumns}
                    selectedColumns={selectedColumns}
                    applySelectedColumns={setSelectedColumns}
                    defaultSelectedColumns={defaultSelectedColumns}
                    maxColumnsAllowed={pickableColumns.length}
                    columnOrder={columnOrder}
                    onColumnOrderChange={onColumnOrderChange}
                    getCSVData={getCSVData}
                    getCSVFileName={getCSVFileName}
                    onSendEmail={onSendEmail}
                    availableEmailDomains={emailDomains}
                    emailInfo={{
                        subject: `${activeType?.labelPlural || "Network entities"} export`,
                        // Explicit: EmailComposerDialog only copies its default
                        // body into the payload as a side effect of the user
                        // editing the subject, so an unedited send arrived with
                        // an empty body.
                        body: `Attached is the ${(activeType?.labelPlural || "network entities").toLowerCase()} list exported from ${ASSET_MANAGEMENT_LABEL}.`,
                    }}
                    // Batch actions stay live with nothing ticked: the natural
                    // default here is "everything I am looking at", and a
                    // permanently greyed Download would read as broken.
                    alwaysEnableActions={true}
                    // Select-all across pages -- see ../selectionHelpers.js.
                    rowSelection={rowSelection}
                    getTotalRowSelection={getTotalRowSelection}
                    getTotalRowSelectionCount={getTotalRowSelectionCount}
                    setSelectedRowKeys={clearSelection}
                    onClearSelection={clearSelection}
                    disablePagination={false}
                    emptyMessage={
                        errorMessage ||
                        (columns.length
                            ? `No ${(activeType?.labelPlural || "entities").toLowerCase()} found`
                            : `Columns are not configured for ${activeType?.tableKey}`)
                    }
                />
            </Box>

            <CreateEditEntityDialog
                open={dialogOpen}
                entityType={activeType}
                entity={dialogEntity}
                onClose={() => {
                    setDialogOpen(false);
                    setDialogEntity(null);
                }}
                onSuccess={() => {
                    setDialogOpen(false);
                    setDialogEntity(null);
                    refetch();
                }}
            />

        </>
    );
};

export default NetworkTabContent;
